import { allocationCellKey } from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import {
  type SolveStatus,
  SolveStatuses,
} from '@ovComponents/4-module/widgets/portfolio/components/lockedMatrixRedistribute';
import { AMOUNT_DERIVED_WIRE_DECIMALS } from '@utils/allocationUnits';

export type UnfundedCell = { sa: string; fp: string; fraction: number };

// Whole wire units, so a balanced column adds up exactly once saved.
const UNITS_PER_WHOLE = 10 ** AMOUNT_DERIVED_WIRE_DECIMALS;

const toUnits = (fraction: number) => Math.round(fraction * UNITS_PER_WHOLE);

const sum = (values: number[]) => values.reduce((total, v) => total + v, 0);

const splitUnitsByWeight = (
  totalUnits: number,
  weighted: { cell: UnfundedCell; weight: number }[],
) => {
  const weightSum = sum(weighted.map(({ weight }) => weight));
  const shares = weighted.map(({ cell, weight }) => {
    const exact = (totalUnits * weight) / weightSum;
    const units = Math.floor(exact);
    return { cell, units, remainder: exact - units };
  });
  let leftover = totalUnits - sum(shares.map(({ units }) => units));
  for (const share of [...shares].sort((a, b) => b.remainder - a.remainder)) {
    if (leftover <= 0) break;
    share.units += 1;
    leftover -= 1;
  }
  return shares;
};

const balanceColumn = (
  cells: UnfundedCell[],
  lockedKeys: ReadonlySet<string>,
  targetUnits: number,
) => {
  const isLocked = (cell: UnfundedCell) =>
    lockedKeys.has(allocationCellKey(cell.sa, cell.fp));
  const locked = cells.filter(isLocked);
  const free = cells.filter((cell) => !isLocked(cell));
  const remainingUnits =
    targetUnits - sum(locked.map((cell) => toUnits(cell.fraction)));
  const balanced = new Map(
    locked.map((cell) => [allocationCellKey(cell.sa, cell.fp), cell.fraction]),
  );

  if (free.length === 0) {
    return Math.abs(remainingUnits) <= locked.length ? balanced : undefined;
  }
  if (remainingUnits < 0) return undefined;

  const hasStartingValues = free.some((cell) => cell.fraction > 0);
  const weighted = free.map((cell) => ({
    cell,
    weight: hasStartingValues ? Math.max(cell.fraction, 0) : 1,
  }));
  for (const { cell, units } of splitUnitsByWeight(remainingUnits, weighted)) {
    balanced.set(allocationCellKey(cell.sa, cell.fp), units / UNITS_PER_WHOLE);
  }

  return balanced;
};

// A $0 account carries no weight in the household's dollar-weighted targets,
// so the only rule for its column is that it adds up to the security share:
// locked cells hold and the free cells share the rest in proportion to their
// starting values.
export const balanceUnfundedColumns = ({
  cells,
  lockedKeys,
  securityMassFraction,
}: {
  cells: UnfundedCell[];
  lockedKeys: ReadonlySet<string>;
  securityMassFraction: number;
}) => {
  const cellsByAccount = new Map<string, UnfundedCell[]>();
  for (const cell of cells) {
    cellsByAccount.set(cell.sa, [...(cellsByAccount.get(cell.sa) ?? []), cell]);
  }

  const targetUnits = toUnits(securityMassFraction);
  const fractions = new Map<string, number>();
  for (const column of cellsByAccount.values()) {
    const balanced = balanceColumn(column, lockedKeys, targetUnits);
    if (!balanced) {
      return {
        status: SolveStatuses.INFEASIBLE as SolveStatus,
        fractions: new Map<string, number>(),
      };
    }
    for (const [key, fraction] of balanced) fractions.set(key, fraction);
  }

  return { status: SolveStatuses.OK as SolveStatus, fractions };
};
